import React from "react";
import StatWrapper from "../shared/StatWrapper";
import Text from "hardcover-ui/components/Text";
import Icon from "hardcover-ui/components/Icon";
import ArrowIcon from "icons/solid/arrow-right.svg";
import { usePage } from "@inertiajs/react";
import { UserStatsPageProps } from "pages/clientverse/users/stats/index.props";
import { audioReadingFormat, textReadingFormat } from "types/ReadingFormatType";
import pageIcon from "icons/solid/book-open.svg";
import LocaleNumber from "components/ui/LocaleNumber";
import Heading from "hardcover-ui/components/Heading";
import pluralize from "lib/pluralize";

interface ReadingPaceStatProps {
  size?: "sm" | "md" | "lg" | "xl";
}

const ReadingPaceStat: React.FC<ReadingPaceStatProps> = ({ size }) => {
  const { user, isOwner, stats } = usePage<UserStatsPageProps>().props;
  const { readingPace: data } = stats;

  if (!data) {
    return (
      <StatWrapper title="Reading Pace" size={size}>
        <div className="flex flex-col w-full gap-4">
          <Heading level={3}>
            {isOwner ? "You haven't" : `${user.name} hasn't`} read any books yet
          </Heading>
        </div>
      </StatWrapper>
    );
  }

  const daysPerBook = data.daysPerBook || 0;
  const hoursTotal = data.hoursTotal || 0;
  const hoursPerYear = data.hoursPerYear || 0;
  const hoursPerMonth = data.hoursPerMonth || 0;
  const hoursPerDay = data.hoursPerDay || 0;
  const pagesTotal = data.pagesTotal || 0;
  const pagesPerYear = data.pagesPerYear || 0;
  const pagesPerMonth = data.pagesPerMonth || 0;
  const pagesPerDay = data.pagesPerDay || 0;
  const booksTotal = data.booksTotal || 0;
  const booksPerYear = data.booksPerYear || 0;
  const booksPerMonth = data.booksPerMonth || 0;
  const booksPerDay = data.booksPerDay || 0;

  return (
    <StatWrapper title="Reading Pace" size={size}>
      <div className="flex flex-col w-full gap-4">
        <div className="flex justify-between items-end">
          <Heading level={3}>
            {isOwner ? "You read" : `${user.name} reads`} a book every{" "}
            <LocaleNumber number={daysPerBook} />{" "}
            {pluralize("Day", daysPerBook)}
          </Heading>
        </div>
        <div className="flex md:flex-col gap-12 w-full mb-8 justify-center">
          {/* Hours */}
          <div className="flex flex-col gap-12 items-center justify-between">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 lg:gap-12">
              <Icon icon={audioReadingFormat.icon} size="xl" />
              <div className="flex flex-col items-center text-center">
                <Text className="text-2xl lg:text-5xl font-bold">
                  <LocaleNumber number={hoursTotal} />
                </Text>
                <Text variant="secondary" size="sm">
                  Hours Read
                </Text>
              </div>

              <Icon
                icon={ArrowIcon}
                className="text-secondary rotate-90 lg:rotate-0"
              />

              <div className="flex flex-col items-center text-center">
                <Text className="text-2xl lg:text-5xl font-bold">
                  <LocaleNumber number={hoursPerYear} />
                </Text>
                <Text variant="secondary" size="sm">
                  PER YEAR
                </Text>
              </div>

              <Icon
                icon={ArrowIcon}
                className="text-secondary rotate-90 lg:rotate-0"
              />

              <div className="flex flex-col items-center text-center">
                <Text className="text-2xl lg:text-5xl font-bold">
                  <LocaleNumber number={hoursPerMonth} />
                </Text>
                <Text variant="secondary" size="sm">
                  PER MONTH
                </Text>
              </div>

              <Icon
                icon={ArrowIcon}
                className="text-secondary rotate-90 lg:rotate-0"
              />

              <div className="flex flex-col items-center text-center">
                <Text className="text-2xl lg:text-5xl font-bold">
                  {hoursPerDay.toFixed(1)}
                </Text>
                <Text variant="secondary" size="sm">
                  PER DAY
                </Text>
              </div>
            </div>
          </div>

          {/* Pages */}
          <div className="flex flex-col gap-12 items-center justify-between">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 lg:gap-12">
              <Icon icon={pageIcon} size="xl" />
              <div className="flex flex-col items-center text-center">
                <Text className="text-2xl lg:text-5xl font-bold">
                  <LocaleNumber number={pagesTotal} />
                </Text>
                <Text variant="secondary" size="sm">
                  Pages Read
                </Text>
              </div>

              <Icon
                icon={ArrowIcon}
                className="text-secondary rotate-90 lg:rotate-0"
              />

              <div className="flex flex-col items-center text-center">
                <Text className="text-2xl lg:text-5xl font-bold">
                  <LocaleNumber number={pagesPerYear} />
                </Text>
                <Text variant="secondary" size="sm">
                  PER YEAR
                </Text>
              </div>

              <Icon
                icon={ArrowIcon}
                className="text-secondary rotate-90 lg:rotate-0"
              />

              <div className="flex flex-col items-center text-center">
                <Text className="text-2xl lg:text-5xl font-bold">
                  <LocaleNumber number={pagesPerMonth} />
                </Text>
                <Text variant="secondary" size="sm">
                  PER MONTH
                </Text>
              </div>

              <Icon
                icon={ArrowIcon}
                className="text-secondary rotate-90 lg:rotate-0"
              />

              <div className="flex flex-col items-center text-center">
                <Text className="text-2xl lg:text-5xl font-bold">
                  <LocaleNumber number={pagesPerDay} />
                </Text>
                <Text variant="secondary" size="sm">
                  PER DAY
                </Text>
              </div>
            </div>
          </div>

          {/* Books */}
          <div className="flex flex-col gap-12 items-center justify-between">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 lg:gap-12">
              <Icon icon={textReadingFormat.icon} size="xl" />
              <div className="flex flex-col items-center text-center">
                <Text className="text-2xl lg:text-5xl font-bold">
                  <LocaleNumber number={booksTotal} />
                </Text>
                <Text variant="secondary" size="sm">
                  Books Read
                </Text>
              </div>

              <Icon
                icon={ArrowIcon}
                className="text-secondary rotate-90 lg:rotate-0"
              />

              <div className="flex flex-col items-center text-center">
                <Text className="text-2xl lg:text-5xl font-bold">
                  <LocaleNumber number={booksPerYear} />
                </Text>
                <Text variant="secondary" size="sm">
                  PER YEAR
                </Text>
              </div>

              <Icon
                icon={ArrowIcon}
                className="text-secondary rotate-90 lg:rotate-0"
              />

              <div className="flex flex-col items-center text-center">
                <Text className="text-2xl lg:text-5xl font-bold">
                  <LocaleNumber number={booksPerMonth} />
                </Text>
                <Text variant="secondary" size="sm">
                  PER MONTH
                </Text>
              </div>

              <Icon
                icon={ArrowIcon}
                className="text-secondary rotate-90 lg:rotate-0"
              />

              <div className="flex flex-col items-center text-center">
                <Text className="text-2xl lg:text-5xl font-bold">
                  <LocaleNumber number={booksPerDay} />
                </Text>
                <Text variant="secondary" size="sm">
                  PER DAY
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StatWrapper>
  );
};

export default ReadingPaceStat;
